import React, { useMemo, useRef, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { TrainingGroupSearchInput } from "../../types/training-group/TGSearchInput";
import {
  getWeekdayByTranslation,
  Weekday,
  WeekdayConfig,
} from "../../types/Weekday";
import { ReactSelectTimePicker } from "../TimePicker";
import { isBefore } from "../../helpers/isBefore";
import { isAfter } from "../../helpers/isAfter";
import { ageRegex } from "../../regex/ageGroupRegex";
import { URLSearchParamsInit, useSearchParams } from "react-router-dom";
import {
  isPostalCodeValid,
  isValidForRegex,
} from "../../helpers/regexValidity";
import {
  getNumberByConditionOrEmptyString,
  getNumberByConditionOrUndefined,
  getStringByConditionOrEmptyString,
} from "../../helpers/getNumberByConditionOrType";
import {
  TrainingGroupComposition,
  TrainingGroupCompositionConfig,
} from "../../types/TrainingGroupComposition";
import ReactSelect from "react-select";
import {
  ClearIndicator,
  ClearIndicatorMulti,
  ClearIndicatorStyles,
} from "../ClearIndicator";
import Creatable from "react-select/creatable";
import { ReducedSearchInput } from "../../types/SearchInput";
import { FormInputField } from "../FormInputField";
import { useEffectOnce, useIsFirstRender, useUpdateEffect } from "usehooks-ts";
import { FilterAltOutline, FilterAltOffOutline } from "../gfx/FilterIcon";
import CloseX from "../gfx/CloseX";

const TWENTY_THREE_HOURS_AND_FOURTY_FIVE_MINUTES_IN_MILLISECONDS = 85500000;

const perimeters = [1, 2, 3, 4, 5, 10];
const perimeterOptions = [
  { value: "county" as number | "county", label: "Gesamter Ort/Landkreis" },
];
perimeterOptions.push(
  ...perimeters.map((value) => {
    return { value: value as number | "county", label: value + " km" };
  }),
);

const booleanSelectValues = ["Ja", "Nein"];

interface Props {
  sports: string[];
  fetchTrainingGroups: (inputs: TrainingGroupSearchInput) => void;
  fetchSports: (inputs: ReducedSearchInput) => void;
  loadingSports: boolean;
  reset: () => void;
  className?: string;
  setError: (error: string) => void;
  runInitialSearch: boolean;
}

type SimpleSearchInput = {
  sportName?: string | null;
  postalCode?: string | null;
  groupComposition?: TrainingGroupComposition;
  age?: number;
};

type ExtendedSearchInput = TrainingGroupSearchInput;

export const TGInputs: React.FC<Props> = ({
  sports,
  fetchTrainingGroups,
  fetchSports,
  loadingSports,
  reset,
  className,
  setError,
  runInitialSearch,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [extendedSearchOpen, setExtendedSearchOpen] = useState(
    searchParams.get("extendedSearch") === "true",
  );

  const [sport, setSport] = useState(
    getStringByConditionOrEmptyString(
      searchParams.get("sportName"),
      (sportName) => isValidForRegex(sportName, /^[A-zÀ-ü0-9.()-_ ]+$/),
    ),
  );
  const [postalCode, setPostalCode] = useState(
    getStringByConditionOrEmptyString(
      searchParams.get("postalCode"),
      (postalCode) => isPostalCodeValid(postalCode),
    ),
  );
  const [perimeter, setPerimeter] = useState((): number | "county" | "" => {
    let areaSearchType = searchParams.get("areaSearchType");
    if (areaSearchType === "COUNTY") {
      return "county";
    }
    return getNumberByConditionOrEmptyString(
      searchParams.get("perimeter"),
      (perimeter) => perimeters.includes(perimeter),
    );
  });
  const [weekdays, setWeekdays] = useState(
    searchParams
      .getAll("weekdays")
      .filter((weekday) => Object.keys(Weekday).includes(weekday)) as Weekday[],
  );
  const [groupComposition, setGroupComposition] = useState(
    (): TrainingGroupComposition | undefined =>
      getStringByConditionOrEmptyString(
        searchParams.get("groupComposition"),
        (groupComposition) => groupComposition in TrainingGroupComposition,
      ) as TrainingGroupComposition,
  );
  const [inclusiveGroup, setInclusiveGroup] = useState(
    (): boolean | undefined => {
      let wheelchairAccessibleVal = searchParams.get("wheelchairAccessible");
      if (wheelchairAccessibleVal === "true") return true;
      if (wheelchairAccessibleVal === "false") return false;
      return undefined;
    },
  );
  const [wheelchairAccessible, setWheelchairAccessible] = useState(
    (): boolean | undefined => {
      let wheelchairAccessibleVal = searchParams.get("wheelchairAccessible");
      if (wheelchairAccessibleVal === "true") return true;
      if (wheelchairAccessibleVal === "false") return false;
      return undefined;
    },
  );
  const [startTime, setStartTime] = useState((): number | undefined => {
    let startTime = getNumberByConditionOrUndefined(
      searchParams.get("startTime"),
      (startTime) => isValidTime(startTime),
    );
    let endTime = getNumberByConditionOrUndefined(
      searchParams.get("endTime"),
      (endTime) => isValidTime(endTime),
    );
    if (startTime && endTime && startTime > endTime) {
      startTime = endTime;
    }
    return startTime;
  });
  const [endTime, setEndTime] = useState((): number | undefined => {
    let startTime = getNumberByConditionOrUndefined(
      searchParams.get("startTime"),
      (startTime) => isValidTime(startTime),
    );
    let endTime = getNumberByConditionOrUndefined(
      searchParams.get("endTime"),
      (endTime) => isValidTime(endTime),
    );
    if (startTime && endTime && endTime < startTime) {
      endTime = startTime;
    }
    return endTime;
  });
  const [age, setAge] = useState(
    getNumberByConditionOrUndefined(searchParams.get("age"), (age) => age >= 0),
  );

  const isFirstRender = useIsFirstRender();

  //being passed to fetch function to differentiate between simple and extended search
  //if extended search options in the ui are open => extended query
  //else => simple query
  const simpleSearchQuery = useMemo(
    () =>
      ({
        sportName: sport,
        postalCode,
        groupComposition,
        age,
      }) as SimpleSearchInput,
    [sport, postalCode, groupComposition, age],
  );
  const extendedSearchQuery = useMemo(
    () => ({
      sportName: sport,
      postalCode,
      groupComposition,
      perimeter,
      weekdays,
      inclusiveGroup,
      wheelchairAccessible,
      startTime,
      endTime,
      age,
    }),
    [
      sport,
      postalCode,
      perimeter,
      weekdays,
      groupComposition,
      inclusiveGroup,
      wheelchairAccessible,
      startTime,
      endTime,
      age,
    ],
  );
  const fetchQuery = useMemo(
    () => (extendedSearchOpen ? extendedSearchQuery : simpleSearchQuery),
    [extendedSearchOpen, simpleSearchQuery, extendedSearchQuery],
  );

  const btnContainerRef = useRef(null);

  useEffectOnce(() => {
    // run the initial search on firstRender if runInitalSearch is on
    if (
      (isFirstRender && runInitialSearch) ||
      // run the inital search if query parameters are submitted too
      /*club ||*/ sport ||
      postalCode ||
      perimeter
    ) {
      validateAndSubmit();
    }
    sportsListNeedUpdate(postalCode, perimeter);
  });

  // check that the current postal code value seems to be valid before trying an update on postalcode change
  useUpdateEffect(() => {
    if (!postalCode || postalCode?.length === 5 || postalCode?.length === 0) {
      sportsListNeedUpdate(postalCode, perimeter);
      validateAndSubmit();
    }
  }, [postalCode]);

  // run an update on perimeter select field input
  useUpdateEffect(() => {
    sportsListNeedUpdate(postalCode, perimeter);
    validateAndSubmit();
  }, [perimeter]);

  // run an update on sport select field input
  useUpdateEffect(() => {
    validateAndSubmit();
  }, [
    sport,
    weekdays,
    groupComposition,
    inclusiveGroup,
    wheelchairAccessible,
    startTime,
    endTime,
    age,
  ]);

  const sportsListNeedUpdate = (
    postalCode: string,
    perimeter: string | number,
  ) => {
    if (isFormValid()) {
      let perimeterValue = null;
      if (perimeter && postalCode) {
        perimeterValue = Number(perimeter);
      }
      fetchSports({
        postalCode: postalCode ?? "",
        perimeter: perimeterValue,
      });
    }
  };

  const validateAndSubmit = () => {
    if (isFormValid()) {
      fetchTrainingGroups(fetchQuery);
      setSearchParams(convertToSearchParams(fetchQuery));
    } else {
      reset();
      updateError();
    }
  };

  const isFormValid = () => {
    let isValid = true;
    if (postalCode && !isPostalCodeValid(postalCode)) {
      isValid = false;
    } else if (
      extendedSearchOpen &&
      perimeter &&
      !(postalCode && isPostalCodeValid(postalCode))
    ) {
      isValid = false;
    } else if (startTime && endTime && startTime > endTime) {
      isValid = false;
    }

    return isValid;
  };

  const updateError = () => {
    if (postalCode && !isPostalCodeValid(postalCode)) {
      setError("Geben Sie eine 5-stellige Postleitzahl ein");
    } else if (
      extendedSearchOpen &&
      perimeter &&
      !(postalCode && isPostalCodeValid(postalCode))
    ) {
      setError("Geben Sie eine 5-stellige Postleitzahl ein");
    } else if (
      extendedSearchOpen &&
      startTime &&
      endTime &&
      startTime > endTime
    ) {
      setError("Die Startzeit kann nicht vor der Endzeit liegen");
    } else {
      setError("");
    }
  };

  return (
    <Form
      className={className || ""}
      onSubmit={(event) => {
        event.preventDefault();
        validateAndSubmit();
      }}
    >
      <Row>
        <Form.Group as={Col} sm={6} lg={5} xxl={3} className="mb-2">
          <Form.Label className="club-sport-label form-label">
            Sportart
          </Form.Label>
          <Creatable
            className="club-sport-select select-font-size"
            isClearable={true}
            isSearchable={true}
            formatCreateLabel={() => ""}
            placeholder={""}
            noOptionsMessage={() => "Keine Sportart gefunden"}
            isLoading={loadingSports}
            defaultValue={sport ? { value: sport, label: sport } : undefined}
            value={sport ? { value: sport, label: sport } : undefined}
            options={sports.map((value, index) => {
              return { value: value, label: value };
            })}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "var(--ver-primary-color)",
                primary75:
                  "color-mix(in srgb,var(--ver-primary-color),#0000 25%)",
                primary50:
                  "color-mix(in srgb,var(--ver-primary-color),#0000 50%)",
                primary25:
                  "color-mix(in srgb,var(--ver-primary-color),#0000 75%)",
              },
            })}
            classNames={{
              control: (state) => (state.isFocused ? "border-shadow" : ""),
            }}
            styles={{
              clearIndicator: ClearIndicatorStyles,
              control: (baseStyles, state) => ({
                ...baseStyles,
                height: "var(--ver-input-height)",
                borderRadius: "var(--bs-border-radius)",
                border: "var(--bs-border-width) solid var(--bs-border-color)",
              }),
              menu: ({ width, ...css }) => ({
                ...css,
                width: "fit-content",
                minWidth: "100%",
              }),
            }}
            onInputChange={(value) => {
              if (value) {
                setSport(value);
              }
            }}
            components={{ ClearIndicator }}
            onChange={(value) => {
              setSport(value?.value ?? "");
            }}
          ></Creatable>
        </Form.Group>
        <Form.Group as={Col} sm={6} lg={2} xxl={2} className="mb-2">
          <Form.Label className="club-postal-code-label form-label">
            Postleitzahl
          </Form.Label>
          <FormInputField
            type="text"
            style={{ paddingRight: "45px", height: "var(--ver-input-height)" }}
            setValue={(value) => {
              if (value?.length > 5 || (value && !value.match(/^[0-9]*$/g))) {
                setPostalCode(postalCode);
              } else {
                setPostalCode(value);
              }
            }}
            value={postalCode}
            className="club-postal-code-input form-input"
          />
        </Form.Group>
        <Col sm={12} md={6} lg={5} xxl={4}>
          <Row>
            <Form.Group as={Col} sm={8} className="mb-2">
              <Form.Label className="club-group-comp-label form-label">
                Geschlecht
              </Form.Label>
              <ReactSelect
                className="club-group-comp-select select-font-size"
                isClearable={true}
                isSearchable={false}
                placeholder={""}
                options={Object.values(TrainingGroupComposition).map(
                  (groupComposition) => {
                    return {
                      value: groupComposition,
                      label:
                        TrainingGroupCompositionConfig[groupComposition]
                          .filterLabel,
                    };
                  },
                )}
                value={
                  groupComposition
                    ? {
                        value: groupComposition,
                        label:
                          TrainingGroupCompositionConfig[
                            groupComposition as TrainingGroupComposition
                          ].filterLabel,
                      }
                    : undefined
                }
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "var(--ver-primary-color)",
                    primary75:
                      "color-mix(in srgb,var(--ver-primary-color),#0000 25%)",
                    primary50:
                      "color-mix(in srgb,var(--ver-primary-color),#0000 50%)",
                    primary25:
                      "color-mix(in srgb,var(--ver-primary-color),#0000 75%)",
                  },
                })}
                classNames={{
                  control: (state) => (state.isFocused ? "border-shadow" : ""),
                }}
                styles={{
                  clearIndicator: ClearIndicatorStyles,
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    height: "var(--ver-input-height)",
                    borderRadius: "var(--bs-border-radius)",
                    border:
                      "var(--bs-border-width) solid var(--bs-border-color)",
                  }),
                }}
                components={{ ClearIndicator }}
                onChange={(value) => setGroupComposition(value?.value)}
              ></ReactSelect>
            </Form.Group>
            <Form.Group as={Col} sm={4} className="mb-2">
              <Form.Label>Alter</Form.Label>
              <FormInputField
                style={{
                  paddingRight: "45px",
                  height: "var(--ver-input-height)",
                }}
                type="text"
                setValue={(value) => {
                  if (value !== undefined && ageRegex.test(value)) {
                    setAge(parseInt(value));
                  } else {
                    setAge(age);
                  }
                }}
                value={age || ""}
                className="club-age-input form-input"
              />
            </Form.Group>
          </Row>
        </Col>
        <Form.Group
          as={Col}
          sm={12}
          md={6}
          lg={12}
          xxl={3}
          className="align-self-end mb-2 mt-3 button-group"
        >
          <div ref={btnContainerRef}></div>
          <OverlayTrigger
            placement={"top"}
            container={btnContainerRef}
            overlay={<Tooltip>Alle Filter löschen</Tooltip>}
          >
            <Button
              className="club-search-button form-button svg-button"
              style={{ display: "flex", alignItems: "center" }}
              variant="outline-primary"
              type="button"
              onClick={(event) => {
                setSearchParams(convertToSearchParams({}));
                reset();
                window.location.reload();
              }}
            >
              <CloseX />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement={"top"}
            container={btnContainerRef}
            overlay={<Tooltip>Erweiterte Suche</Tooltip>}
          >
            <Button
              className="club-search-button form-button svg-button"
              style={{ display: "flex", alignItems: "center" }}
              variant="outline-primary"
              type="button"
              onClick={(event) => {
                setExtendedSearchOpen(!extendedSearchOpen);
              }}
            >
              <FilterAltOutline
                style={{ display: extendedSearchOpen ? "none" : "inherit" }}
              />
              <FilterAltOffOutline
                style={{ display: extendedSearchOpen ? "inherit" : "none" }}
              />
            </Button>
          </OverlayTrigger>
          <Button
            className="club-search-button form-button text-white"
            variant="primary"
            onClick={(event) => {
              event.currentTarget.blur();
            }}
            type="submit"
          >
            Suchen
          </Button>
        </Form.Group>
      </Row>
      <Collapse in={extendedSearchOpen}>
        <Row id="collapsable" className="mt-2">
          <Col xs={12} xxl={5}>
            <Row>
              <Form.Group as={Col} xs={4} md={3} xxl={4} className="mb-2">
                <Form.Label className="club-perimeter-label form-label">
                  Umkreis
                </Form.Label>
                <ReactSelect
                  className="club-perimeter-select select-font-size"
                  isClearable={true}
                  isSearchable={false}
                  placeholder={""}
                  defaultValue={perimeterOptions.find(
                    (value, index) => perimeter === value.value,
                  )}
                  options={perimeterOptions}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: "var(--ver-primary-color)",
                      primary75:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 25%)",
                      primary50:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 50%)",
                      primary25:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 75%)",
                    },
                  })}
                  classNames={{
                    control: (state) =>
                      state.isFocused ? "border-shadow" : "",
                  }}
                  styles={{
                    clearIndicator: ClearIndicatorStyles,
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      height: "var(--ver-input-height)",
                      borderRadius: "var(--bs-border-radius)",
                      border:
                        "var(--bs-border-width) solid var(--bs-border-color)",
                    }),
                  }}
                  components={{ ClearIndicator }}
                  onChange={(value) => setPerimeter(value?.value ?? "")}
                ></ReactSelect>
              </Form.Group>
              <Form.Group as={Col} xs={8} md={9} xxl={8} className="mb-2">
                <Form.Label className="club-weekday-label form-label">
                  Wochentag
                </Form.Label>
                <ReactSelect
                  className="club-weekday-select select-font-size"
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  isClearable={true}
                  isSearchable={false}
                  isMulti={true}
                  placeholder={""}
                  options={Object.values(WeekdayConfig).map((weekday) => {
                    return {
                      value: getWeekdayByTranslation(weekday.translation),
                      label: weekday.translation,
                    };
                  })}
                  value={
                    weekdays
                      ? weekdays.map((weekday) => {
                          return {
                            value: weekday,
                            label: WeekdayConfig[weekday].translation,
                          };
                        })
                      : undefined
                  }
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: "var(--ver-primary-color)",
                      primary75:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 25%)",
                      primary50:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 50%)",
                      primary25:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 75%)",
                    },
                  })}
                  classNames={{
                    control: (state) =>
                      state.isFocused ? "border-shadow" : "",
                  }}
                  styles={{
                    clearIndicator: ClearIndicatorStyles,
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      height: "var(--ver-input-height)",
                      borderRadius: "var(--bs-border-radius)",
                      border:
                        "var(--bs-border-width) solid var(--bs-border-color)",
                      flexWrap: "nowrap",
                    }),
                    valueContainer: (baseStyles, state) => ({
                      ...baseStyles,
                      flexWrap: "nowrap",
                    }),
                  }}
                  components={{ ClearIndicator: ClearIndicatorMulti }}
                  onChange={(value) => {
                    let newMap = value
                      ? value.map((weekday) =>
                          getWeekdayByTranslation(weekday.label),
                        )
                      : ([] as Weekday[]);
                    setWeekdays(newMap);
                  }}
                ></ReactSelect>
              </Form.Group>
            </Row>
          </Col>
          <Col md={6} xxl={3}>
            <Form.Label>Beginn zwischen</Form.Label>
            <Row className="align-items-center">
              <Form.Group as={Col} className="mb-2">
                <ReactSelectTimePicker
                  className="club-time-picker select-font-size"
                  onChange={(time: number) => {
                    //if selected time is after "end" time then set it to end time, else set selected time
                    setStartTime(
                      isAfter(time * 1000, endTime) ? endTime : time * 1000,
                    );
                  }}
                  value={(startTime || 0) / 1000}
                />
              </Form.Group>
              <Form.Group as={Col} className="mb-2 p-0 flex-grow-0">
                <Form.Label>-</Form.Label>
              </Form.Group>
              <Form.Group as={Col} className="mb-2">
                <ReactSelectTimePicker
                  className="club-time-picker select-font-size"
                  onChange={(time: number) => {
                    //if selected time is before "start" time then set it to start time, else set selected time
                    setEndTime(
                      isBefore(time * 1000, startTime)
                        ? startTime
                        : time * 1000,
                    );
                  }}
                  value={
                    (endTime ||
                      TWENTY_THREE_HOURS_AND_FOURTY_FIVE_MINUTES_IN_MILLISECONDS) /
                    1000
                  }
                />
              </Form.Group>
            </Row>
          </Col>
          <Col md={6} xxl={4}>
            <Row>
              <Form.Group as={Col} xs={6} className="mb-2">
                <Form.Label className="club-inclusive-label form-label">
                  Inklusivsport
                </Form.Label>
                <ReactSelect
                  className="club-inclusive-select select-font-size"
                  isClearable={true}
                  isSearchable={false}
                  placeholder={""}
                  defaultValue={
                    inclusiveGroup !== undefined
                      ? {
                          value: inclusiveGroup,
                          label: getStringFromBoolean(inclusiveGroup),
                        }
                      : undefined
                  }
                  options={booleanSelectValues.map((val, index) => {
                    return {
                      value: getBooleanFromString(val) as boolean,
                      label: val,
                    };
                  })}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: "var(--ver-primary-color)",
                      primary75:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 25%)",
                      primary50:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 50%)",
                      primary25:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 75%)",
                    },
                  })}
                  classNames={{
                    control: (state) =>
                      state.isFocused ? "border-shadow" : "",
                  }}
                  styles={{
                    clearIndicator: ClearIndicatorStyles,
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      height: "var(--ver-input-height)",
                      borderRadius: "var(--bs-border-radius)",
                      border:
                        "var(--bs-border-width) solid var(--bs-border-color)",
                    }),
                  }}
                  components={{ ClearIndicator }}
                  onChange={(value) =>
                    setInclusiveGroup(value?.value ?? undefined)
                  }
                ></ReactSelect>
              </Form.Group>
              <Form.Group as={Col} xs={6} className="mb-2">
                <Form.Label className="club-wheelchair-label form-label">
                  Rollstuhlgerecht
                </Form.Label>
                <ReactSelect
                  className="club-wheelchair-select select-font-size"
                  isClearable={true}
                  isSearchable={false}
                  placeholder={""}
                  defaultValue={
                    wheelchairAccessible !== undefined
                      ? {
                          value: wheelchairAccessible,
                          label: getStringFromBoolean(wheelchairAccessible),
                        }
                      : undefined
                  }
                  options={booleanSelectValues.map((val, index) => {
                    return {
                      value: getBooleanFromString(val) as boolean,
                      label: val,
                    };
                  })}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: "var(--ver-primary-color)",
                      primary75:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 25%)",
                      primary50:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 50%)",
                      primary25:
                        "color-mix(in srgb,var(--ver-primary-color),#0000 75%)",
                    },
                  })}
                  classNames={{
                    control: (state) =>
                      state.isFocused ? "border-shadow" : "",
                  }}
                  styles={{
                    clearIndicator: ClearIndicatorStyles,
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      height: "var(--ver-input-height)",
                      borderRadius: "var(--bs-border-radius)",
                      border:
                        "var(--bs-border-width) solid var(--bs-border-color)",
                    }),
                  }}
                  components={{ ClearIndicator }}
                  onChange={(value) =>
                    setWheelchairAccessible(value?.value ?? undefined)
                  }
                ></ReactSelect>
              </Form.Group>
            </Row>
          </Col>
        </Row>
      </Collapse>
    </Form>
  );
};

//convert inputs to a valid URLSearchParamsInit Object: Record<String, String | String[]>
const convertToSearchParams = (
  input: ExtendedSearchInput | SimpleSearchInput,
) => {
  const URLSearchParamsInit: URLSearchParamsInit = {};
  if (input.sportName) URLSearchParamsInit.sportName = input.sportName;
  if (input.postalCode) URLSearchParamsInit.postalCode = input.postalCode;
  if (input.age) URLSearchParamsInit.age = input.age + "";
  if (input.groupComposition)
    URLSearchParamsInit.groupComposition = input.groupComposition;
  if (isExtendedSearchInput(input)) {
    const extendedSearchInput = input as ExtendedSearchInput;
    URLSearchParamsInit.extendedSearch = "true";
    if (extendedSearchInput.perimeter)
      URLSearchParamsInit.perimeter = extendedSearchInput.perimeter + "";
    if (typeof extendedSearchInput.inclusiveGroup !== "undefined")
      URLSearchParamsInit.inclusiveGroup =
        extendedSearchInput.inclusiveGroup + "";
    if (typeof extendedSearchInput.wheelchairAccessible !== "undefined")
      URLSearchParamsInit.wheelchairAccessible =
        extendedSearchInput.wheelchairAccessible + "";
    if (extendedSearchInput.startTime)
      URLSearchParamsInit.startTime = extendedSearchInput.startTime + "";
    if (extendedSearchInput.endTime)
      URLSearchParamsInit.endTime = extendedSearchInput.endTime + "";
    if (extendedSearchInput.weekdays)
      URLSearchParamsInit.weekdays = extendedSearchInput.weekdays.map(
        (weekday) => Weekday[weekday],
      );
  }
  return URLSearchParamsInit;
};

function isExtendedSearchInput(
  input: TrainingGroupSearchInput | SimpleSearchInput,
) {
  //if weekdays exists in input, that means that this is an extended search input
  return "weekdays" in input;
}

function isValidTime(time: number) {
  return (
    time >= 0 &&
    time <= TWENTY_THREE_HOURS_AND_FOURTY_FIVE_MINUTES_IN_MILLISECONDS
  );
}

function getStringFromBoolean(
  bool: boolean | undefined,
): string | number | readonly string[] | undefined {
  if (typeof bool === "undefined") return "";
  return bool ? "Ja" : "Nein";
}

function getBooleanFromString(str: string) {
  if (!str) return undefined;
  if (str === "Ja") return true;
  else return false;
}
